// src/components/OnceOffPaymentReturnPage.jsx
// Purpose: Handles PayFast return redirects for once-off payments.
// App route:
//   /payment/onceoff-return

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOnceOffPurchaseStatus,
  hasOnceOffJourneyAccessForCurrentUser,
  logMyOnceOffPaymentEvent,
} from "../../lib/payments/onceOffPayments";

export default function OnceOffPaymentReturnPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const purchaseId = params.get("purchase_id") || "";
  const paymentResult = params.get("payment") || "";

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);

      if (!purchaseId) {
        if (!cancelled) {
          setStatus({
            status: "missing",
            payment_status: "missing",
          });
          setLoading(false);
        }

        return;
      }

      await logMyOnceOffPaymentEvent({
        eventType: "onceoff_return_page_seen",
        eventStatus: "returned",
        purchaseId,
        productCode: "gwin_60_day_journey_q2_q5",
        purchaseStatus: "returned",
        message: "User returned to the app after once-off PayFast checkout.",
        metadata: {
          source_page: "OnceOffPaymentReturnPage",
          payment_result: paymentResult,
          search: location.search,
          url: window.location.href,
        },
      });

      const purchaseStatus = await getOnceOffPurchaseStatus(purchaseId);
      const access = await hasOnceOffJourneyAccessForCurrentUser();

      await logMyOnceOffPaymentEvent({
        eventType: "onceoff_return_status_checked",
        eventStatus: purchaseStatus?.status || "unknown",
        purchaseId,
        productCode: "gwin_60_day_journey_q2_q5",
        purchaseStatus: purchaseStatus?.status || null,
        message: "Frontend checked once-off payment status after PayFast return.",
        metadata: {
          source_page: "OnceOffPaymentReturnPage",
          purchase_status: purchaseStatus,
          has_access: access === true,
        },
      });

      if (!cancelled) {
        setStatus(purchaseStatus || { status: "processing" });
        setHasAccess(access === true);
        setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [purchaseId, paymentResult, location.search]);

  const goDashboard = () => navigate("/dashboard", { replace: true });
  const goProgress = () => navigate("/progress-dashboard", { replace: true });

  const normalStatus = String(status?.status || "").toLowerCase();
  const normalPaymentStatus = String(status?.payment_status || "").toLowerCase();

  const completed =
    hasAccess ||
    normalStatus === "completed" ||
    normalStatus === "complete" ||
    normalPaymentStatus === "complete" ||
    normalPaymentStatus === "completed";

  const failed =
    normalStatus === "failed" ||
    normalPaymentStatus === "failed" ||
    normalStatus === "cancelled" ||
    normalStatus === "canceled";

  let title = "Checking payment";
  let message = "Please wait while we check your PayFast payment status.";
  let icon = "⏳";

  if (!loading) {
    if (completed) {
      title = "Payment successful";
      message =
        "Your 60-Day Business Finance Journey access has been unlocked. You can continue from Quest 2 through Quest 5.";
      icon = "✅";
    } else if (failed) {
      title = "Payment not completed";
      message =
        "PayFast did not confirm this payment. Please try again or contact support if money left your account.";
      icon = "⚠️";
    } else if (normalStatus === "missing") {
      title = "Payment reference missing";
      message =
        "We could not find the payment reference in the return link. Please go back to your dashboard and try again.";
      icon = "⚠️";
    } else {
      title = "Payment still processing";
      message =
        "We received your PayFast return, but the payment is still processing. Confirmation can take 5–10 minutes. Your access will unlock once PayFast confirms the payment.";
      icon = "⏳";
    }
  }

  return (
    <div className="min-h-screen bg-[#FFF7ED] px-4 py-8 font-sans">
      <div className="mx-auto flex min-h-[70vh] max-w-md items-center justify-center">
        <div className="w-full rounded-3xl bg-white p-6 text-center shadow-xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF7ED] text-3xl">
            {icon}
          </div>

          <h1 className="text-2xl font-bold text-[#111827]">{title}</h1>

          <p className="mt-3 text-sm leading-6 text-[#4B5563]">{message}</p>

          {purchaseId ? (
            <p className="mt-4 break-all rounded-xl bg-gray-50 p-3 text-xs text-gray-500">
              Reference: {purchaseId}
            </p>
          ) : null}

          <div className="mt-6 flex flex-col gap-3">
            {completed ? (
              <button
                type="button"
                onClick={goProgress}
                className="rounded-2xl bg-[#FF914D] px-4 py-3 font-semibold text-white shadow"
              >
                Continue journey
              </button>
            ) : null}

            <button
              type="button"
              onClick={goDashboard}
              className="rounded-2xl border border-gray-200 px-4 py-3 font-semibold text-gray-700"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}